import {service as io} from './streamingService';
import {User} from './userService';
import {Observer} from '../utils';
import i18next from "i18next";

let nextRequestId = (new Date()).valueOf();
function getNextRequestId(userId) {
    var requestId = userId + "TD" + String(nextRequestId);
    nextRequestId++;
    return requestId;
}

// reduce the noise in SSIs - e.g. wire info
function getSsi(ssi) {
    return {
        ccy: ssi.ccy,
        ssid: ssi.ssid + "",
        name: ssi.name
    };
}

// return yyyymmdd from a date object - if we user .toJSON() timezones mess it up
function getDateString(value) {
    if (value instanceof Date && value > 0) {
        return `${value.getFullYear()}${(value.getMonth() + 1).toString().padStart(2, '0')}${(value.getDate()).toString().padStart(2, '0')}`;
    } else if (typeof value === 'string') {
        // amend supplies a string like "2026-06-26" - normalise to "20260626"
        return value.replace(/-/g, '');
    } else {
        return value;
    }
}

function buildRequest(data) {
    const canSettle = User.user.permissions.SETTLE;

    let request = {
        state: 'Open',
        wantFwdPoints: User.user.permissions.SHOWFWDPOINTS,
        wantMidPoints: User.user.permissions.SHOWMIDPOINTS,
        action: data.product,
        buyCurrency: data.buyCurrency,
        sellCurrency: data.sellCurrency,
        ...(data.buyAccount && canSettle && {buyAccount: getSsi(data.buyAccount)}),
        ...(data.sellAccount && canSettle && {sellAccount: getSsi(data.sellAccount)}),
        ...(data.valueDateType && {valueDateType: data.valueDateType}),
        ...(data.valueDateDate && {valueDateDate: getDateString(data.valueDateDate)}),
        buyAmount: data.buyAmount ? data.buyAmount : 0,
        sellAmount: data.sellAmount ? data.sellAmount : 0,
        inverseQuote: data.inverseQuote,
        requestId: getNextRequestId(User.user.userId),
        tradeMemo: data.tradeMemo,
        wireMemo: data.wireMemo,
        company: {
            code: data.company?.code,
            name: data.company?.name,
            desk: data.company?.deskName
        },
        tradeOnly: User.canSettle(),
        userName: User.user.fullName
    };
    return request;
}

const QUOTE_TIMEOUT_SECONDS = 30;

function ScheduledSpotService() {
    const STATES = {
        INITIAL: 'Initial',
        // we sent it
        OPEN: 'Open',
        // back end did it
        SCHEDULED: 'Scheduled',
        // back end didn't like it
        SCHEDULE_REJECTED: 'ScheduleRejected',
        // We changed our mind
        TIMEOUT: 'Timeout',
        CLIENT_REJECTED: 'ClientRejected',
    };
    this.STATES = STATES;
    this.state = this.STATES.INITIAL;
    this.errors = [];
    this.request = null;
    this.listenerHandle = null;
    this.decrementQuoteCountdown = QUOTE_TIMEOUT_SECONDS;
    this.cancelInProgress = false;

    this.scheduledSpots = [];
    this.getScheduledSpots = function() {
        return this.scheduledSpots;
    };

    // trade[0] should be the newest
    function sortScheduledSpots(theScheduledSpots) {
        theScheduledSpots?.sort((a, b) => new Date(b.order?.createdAt) - new Date(a.order?.createdAt));
    }

    function search(searchData, callback) {
        // if we disconnect before we get all the data, the handler sticks around
        io.emit('scheduledSpotSearch', searchData, (data) => {
            processSearchEvents(data, callback);
        }, 'scheduledSpotSearch_' + searchData.type);
    }

    function shouldMask(item) {
        return !User.isInternalUser()
            && item.userId
            && (item.userId.includes("@TDSECURITIES.COM") || item.userId === "MUREX");
    }

    function processSearchEvents(res, callback) {
        const orders = res?.data?.orders ?? [];
        if (orders?.length > 0) {
            orders.forEach(function(item, index){
                item.product = "SCHEDULED_SPOT";
                item.productDesc = i18next.t("td.fx.blotter.label.SSP");
                item.status = item.state;
                item.valueDate = item.executionDate;
                if (item.sellCurrency === item.currency) {
                    item.sellAmount = item.quantity;
                } else if (item.buyCurrency === item.currency) {
                    item.buyAmount = item.quantity;
                }
                item.buyAccountId = item.buySsi?.reference;
                item.buyAccount = item.buySsi?.accountName;
                item.sellAccountId = item.sellSsi?.reference;
                item.sellAccount = item.sellSsi?.accountName;
                item.tradeMemo = item.notes;
                item.userId = shouldMask(item) ? i18next.t("fx.blotter.phoneDealMask") : item.userId;
                item.inverseQuote = item.priceType === "INVERTED";
                item.creationDate = item.modificationTime?.split('T')[0];
            });
        }
        let more = res?.data?.more ?? false;
        callback(orders, more);
    }

    this.loaded = this.loading = false;
    this.load = function() {
        if (!this.loaded) {
            this.loaded = true;
            this.loading = true;
            let pubCount = 0;
            let pubInterval = 2;
            search({type: 'EVERYTHING'}, (data, more) => {
                // initial load we don't have to merge?
                this.scheduledSpots.push(...data);
                if (!more) {
                    // sort by creation date - not clear that the search returns them this way
                    sortScheduledSpots(this.scheduledSpots);
                    this.loading = false;
                    this.initialLoad = true;
                }
                // don't always publish when we have more
                // as we have more scheduled spots, the cost of an update gets bigger
                // so we don't want straight linear updates
                if (!more ||  !(pubCount % pubInterval)) {
                    pubInterval = pubInterval * 3;
                    pubCount = 1;
                    this.publish();
                } else {
                    pubCount = pubCount + 1;
                }
            });
        }
    };

    this.reload = function() {
        if (!this.loading) {
            this.scheduledSpots = [];
            this.publish();
            this.loaded = false;
            this.load();
        }
    };

    let getLatestScheduledSpotModification = function(theScheduledSpots) {
        const tradesWithModificationTime = (theScheduledSpots || []).filter(
            (trade) => trade?.modificationTime
        );
        const newestTrade = tradesWithModificationTime.length
            ? tradesWithModificationTime.reduce((result, current) =>
                result.modificationTime > current.modificationTime ? result : current
            )
            : undefined;
        if (!newestTrade) {
            // we know we loaded tdfx today and did a search already
            // so search from start of day
            let today = new Date();
            today.setHours(0, 0, 0, 0);
            return today.getTime();
        } else {
            return newestTrade.modificationTime;
        }
    };

    let mergeData = function(data, target) {
        for (let i = 0; i < data.length; i++) {
            const incoming = data[i];
            if (!incoming?.orderId) {
                target.unshift(incoming);
            } else {
                const existingIndex = target.findIndex((item) => item?.orderId === incoming?.orderId);
                if (existingIndex >= 0) {
                    target[existingIndex] = incoming;
                } else {
                    target.unshift(incoming);
                }
            }
        }
    };

    this.searchForNewTrades = function() {
        let lastModifiedTime =  new Date(getLatestScheduledSpotModification(this.scheduledSpots));
        search({type: 'LATEST', query: {lastModifiedTime: lastModifiedTime}}, (data, more) => {
            mergeData(data, this.scheduledSpots);
            // we don't want to call this unless we're the last one
            if (!more) {
                // callback could be another search, so clear the flag first
                this.loading = false;
                this.publish();
            }
        });

    };

    this.update = function() {
        if (!this.initialLoad) {
            this.reload();
        } else if (this.loaded && !this.loading) {
            this.loading = true;
            this.searchForNewTrades(this.scheduledSpots);
        }
    };

    this.isWorking = function () {
        return this.state === this.STATES.OPEN;
    };

    this.isWaitingForScheduledSpot = function () {
        return this.state === this.STATES.OPEN || this.cancelInProgress;
    };

    this.isCancellingScheduledSpot = function () {
        return this.cancelInProgress;
    };

    this.isAmendingScheduledSpot = function () {
        return this.scheduledSpotToAmend !== undefined || this.cancelInProgress;
    };

    this.isConcluded = function () {
        return this.state === this.STATES.SCHEDULED
            || this.state === this.STATES.SCHEDULE_REJECTED
            || this.state === this.STATES.TIMEOUT;
    };

    this.isScheduled = function () {
        return this.state === this.STATES.SCHEDULED;
    };

    this.isRejected = function () {
        return this.state === this.STATES.SCHEDULE_REJECTED;
    };

    this.isTimedOut = function () {
        return this.state === this.STATES.TIMEOUT;
    };

    this.getErrors = function () {
        return this.errors;
    };

    this.clearResult = function () {
        this.state = this.STATES.INITIAL;
        this.errors = [];
        this.request = null;
        this.removeAmendScheduledSpotResultListener();
        this.publish();
    };

    this.newRequest = function () {
        this.request = null;
        this.state = this.STATES.INITIAL;
        this.errors = [];
        this.removeAmendScheduledSpotResultListener();
        this.publish();
    };

    this.createScheduledSpotResultListener = function(clear) {
        this.removeScheduledSpotResultListener();
        this.listenerHandle = io.createListener(
            'scheduledSpotResult',
            (response) => {
                if (this.state === STATES.OPEN
                        && response.data.requestId
                        && (response.data.requestId.startsWith(this.request?.requestId))
                ) {
                    // Check if response has errors
                    if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                        this.state = this.STATES.SCHEDULE_REJECTED;
                        this.errors = response.errors;
                    } else {
                        this.state = this.STATES.SCHEDULED;
                        this.errors = [];
                        // Load this scheduled spot and perhaps more recent spots scheduled by others into the blotter
                        clear();
                        this.update();
                    }
                    this.cancelTimeout();
                    this.publish();
                    this.removeScheduledSpotResultListener();
                    this.clearScheduledSpotToAmend();
                }
            }
        );
    };

    this.createAmendScheduledSpotResultListener = function() {
        this.removeAmendScheduledSpotResultListener();
        this.amendListenerHandle = io.createListener(
            'amendScheduledSpotResult',
            (response) => {
                if (this.state === STATES.OPEN
                        && response.data.requestId
                        && (response.data.requestId.startsWith(this.request?.requestId))
                ) {
                    if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                        this.state = this.STATES.SCHEDULE_REJECTED;
                        this.errors = response.errors;
                    } else {
                        this.state = this.STATES.SCHEDULED;
                        this.errors = [];
                        this.update();
                    }
                    this.cancelTimeout();
                    this.publish();
                    this.removeAmendScheduledSpotResultListener();
                }
            }
        );
    };

    this.removeScheduledSpotResultListener = function () {
        if (this.listenerHandle) {
            io.socket?.removeListener('scheduledSpotResult', this.listenerHandle);
            this.listenerHandle = false;
        }
    };

    this.removeAmendScheduledSpotResultListener = function () {
        if (this.amendListenerHandle) {
            io.socket?.removeListener('amendScheduledSpotResult', this.amendListenerHandle);
            this.amendListenerHandle = false;
        }
    };

    this.cancelTimeout = function () {
        if (this.timeoutTimer) {
            clearTimeout(this.timeoutTimer);
        }
    };

    this.timeoutQuote = function () {
        this.state = this.STATES.TIMEOUT;
        this.removeScheduledSpotResultListener();
        this.removeAmendScheduledSpotResultListener();
        this.publish();
    };

    this.submitScheduledSpot = function (data) {
        let request = buildRequest(data);
        this.request = data;
        data.requestId = request.requestId;

        this.state = this.STATES.OPEN;
        this.createScheduledSpotResultListener(data.clearScheduledSpotToAmend);
        io.emit('scheduledSpot', request);

        const decrementQuoteTimeout = () => {
            this.decrementQuoteCountdown = this.decrementQuoteCountdown - 1;
            if (this.decrementQuoteCountdown === 0 && this.state === this.STATES.OPEN) {
                this.timeoutQuote();
            } else {
                this.timeoutTimer = setTimeout(decrementQuoteTimeout, 1000);
            }
            this.publish();
        };
        decrementQuoteTimeout();
    };

    this.amendScheduledSpot = function (data) {
        let request = buildRequest(data);
        this.clearScheduledSpotToAmend();
        request.orderId = data?.orderId;
        this.request = data;
        data.requestId = request.requestId;

        this.state = this.STATES.OPEN;
        this.createAmendScheduledSpotResultListener();
        io.emit('amendScheduledSpot', request);

        const decrementQuoteTimeout = () => {
            this.decrementQuoteCountdown = this.decrementQuoteCountdown - 1;
            if (this.decrementQuoteCountdown === 0 && this.state === this.STATES.OPEN) {
                this.timeoutQuote();
            } else {
                this.timeoutTimer = setTimeout(decrementQuoteTimeout, 1000);
            }
            this.publish();
        };
        decrementQuoteTimeout();
    };

    this.clientReject = function() {
        this.state = this.STATES.CLIENT_REJECTED;
        this.removeScheduledSpotResultListener();
        this.removeAmendScheduledSpotResultListener();
        this.cancelTimeout();
        this.publish();
    };

    this.cancelScheduledSpot = function(orderId) {
        if (!orderId) {
            return;
        }

        this.cancelInProgress = true;
        this.publish();

        const request = {
            orderId: orderId,
            requestId: getNextRequestId(User.user.userId)
        };

        const cancelResultListener = io.createListener('cancelScheduledSpotResult', (result) => {
            this.cancelInProgress = false;
            this.update();
            this.publish();
            io.socket?.removeListener('cancelScheduledSpotResult', cancelResultListener);
        });

        io.emit('cancelScheduledSpot', request);
    };

    this.loadScheduledSpot = function(scheduledSpotToAmend) {
        this.scheduledSpotToAmend = scheduledSpotToAmend;
        this.publish();
    };

    this.clearScheduledSpotToAmend = function() {
        this.scheduledSpotToAmend = undefined;
        this.publish();
    };

    // we want observers to be fired
    Observer.mixin(this);

    // subscribe to the user service - we get updates on our connection there
    User.subscribe(() => {
        if (!User.user) {
            this.scheduledSpots = [];
            this.loaded = false;
            this.loading = false;
            this.initialLoad = false;
        } else if (!User.connected) {
            if (this.isWorking()) {
                this.state = this.STATES.TIMEOUT;
                this.removeScheduledSpotResultListener();
                this.publish();
            }
            this.loading = false;
        } else {
            this.reload();
        }
    });
}

const ScheduledSpot = new ScheduledSpotService();

export { ScheduledSpot };
